import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BALANCE_ENTRY_KINDS, RATE_SOURCES, type BalanceEntryKind, type RateSource } from '@subs/shared';

import { balance, type BalanceEntry, type BalanceEntryInput } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Button, Field, Input, Modal, Provenance, Select, Textarea } from '../../components/ui';
import { formatAmount } from '../../lib/format';
import { isoDateOnly } from '../../lib/query-params';

/**
 * Create/edit form for one ledger row (`LLM_CONTEXT/11-balance.md` §5). The
 * modal is shared by "Add funding" and a row's Edit: `entry === null` means
 * create. There is no `rubAmount` field on purpose — the API derives it.
 */

interface FormState {
  kind: BalanceEntryKind;
  /** Kept as text so an empty field is distinguishable from `0`. */
  amount: string;
  currency: string;
  fxRate: string;
  rateSource: RateSource | '';
  occurredAt: string;
  note: string;
}

const EMPTY: FormState = {
  kind: 'FUNDING',
  amount: '',
  currency: 'RUB',
  fxRate: '',
  rateSource: '',
  occurredAt: '',
  note: '',
};

/** `<input type="date">` value for an ISO timestamp (UTC, like `formatDate`). */
function toDateInput(iso: string): string {
  return iso.slice(0, 10);
}

function fromEntry(entry: BalanceEntry): FormState {
  return {
    kind: entry.kind,
    amount: String(entry.amount),
    currency: entry.currency,
    fxRate: entry.fxRate === null ? '' : String(entry.fxRate),
    rateSource: entry.rateSource ?? '',
    occurredAt: toDateInput(entry.occurredAt),
    note: entry.note ?? '',
  };
}

/**
 * Mirrors the API's own rules: `FUNDING` > 0, a signed `CORRECTION` (never 0),
 * and an auditable rate for anything that is not RUB.
 */
function validate(state: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const amount = Number(state.amount);
  if (state.amount.trim() === '' || !Number.isFinite(amount)) errors.amount = 'Amount is required';
  else if (state.kind === 'FUNDING' && amount <= 0) errors.amount = 'Funding must be greater than zero';
  else if (state.kind === 'CORRECTION' && amount === 0) errors.amount = 'A correction is positive or negative, never zero';

  const currency = state.currency.trim().toUpperCase();
  if (currency.length !== 3) errors.currency = '3-letter code, e.g. RUB';

  if (currency !== 'RUB') {
    const rate = Number(state.fxRate);
    if (state.fxRate.trim() === '' || !Number.isFinite(rate) || rate <= 0) {
      errors.fxRate = `An FX rate (₽ per ${currency}) is required for a ${currency} entry`;
    }
    if (!state.rateSource) errors.rateSource = 'Say where the rate came from';
  }

  if (!state.occurredAt) errors.occurredAt = 'Date is required';
  return errors;
}

export function BalanceEntryModal({
  open,
  entry,
  onClose,
}: {
  open: boolean;
  /** `null` = create. */
  entry: BalanceEntry | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState(false);

  // Re-seed on every open: a fresh row starts as a RUB funding dated today.
  const seedKey = `${entry?.id ?? 'new'}-${open}`;
  useEffect(() => {
    if (!open) return;
    setState(entry ? fromEntry(entry) : { ...EMPTY, occurredAt: isoDateOnly() });
    setTouched(false);
  }, [seedKey]);

  const mutation = useMutation({
    mutationFn: (form: FormState) => {
      const isRub = form.currency.trim().toUpperCase() === 'RUB';
      const payload: BalanceEntryInput = {
        kind: form.kind,
        amount: Number(form.amount),
        currency: form.currency.trim().toUpperCase(),
        occurredAt: new Date(`${form.occurredAt}T00:00:00.000Z`).toISOString(),
        // Absent keys are dropped by JSON.stringify, so a RUB row never carries
        // a rate and switching back to RUB on edit clears the old one.
        fxRate: isRub ? undefined : Number(form.fxRate),
        rateSource: isRub ? undefined : (form.rateSource as RateSource),
        note: form.note.trim() || null,
      };
      return entry ? balance.updateEntry(entry.id, payload) : balance.createEntry(payload);
    },
    onSuccess: (saved) => {
      // The totals and the paged rows both come from `GET /balance`.
      void queryClient.invalidateQueries({ queryKey: ['balance'] });
      toast(entry ? 'Ledger entry updated' : `Recorded ${formatAmount(saved.rubAmount, 'RUB')}`, 'success');
      onClose();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setState((current) => ({ ...current, [field]: value }));

  const errors = validate(state);
  const show = (field: keyof FormState): string | undefined => (touched ? errors[field] : undefined);
  const isRub = state.currency.trim().toUpperCase() === 'RUB';

  const submit = () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    mutation.mutate(state);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={entry ? 'Edit ledger entry' : 'Add funding'}
      description={
        entry
          ? 'Corrections and rates can be fixed in place — the balance is recomputed on read.'
          : 'Money the company handed over. Use CORRECTION to claw one back without rewriting history.'
      }
      footer={
        <>
          <Provenance createdBy={entry?.createdBy ?? null} createdAt={entry?.createdAt ?? null} updatedBy={entry?.updatedBy ?? null} />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={submit}>
            {entry ? 'Save changes' : 'Record entry'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Kind" htmlFor="balance-kind" hint="A refund of a funding is a negative CORRECTION">
          <Select
            id="balance-kind"
            value={state.kind}
            onChange={(event) => set('kind', event.target.value as BalanceEntryKind)}
          >
            {BALANCE_ENTRY_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Amount" htmlFor="balance-amount" required error={show('amount')}>
          <Input
            id="balance-amount"
            type="number"
            step="0.01"
            value={state.amount}
            aria-invalid={Boolean(show('amount'))}
            onChange={(event) => set('amount', event.target.value)}
            placeholder={state.kind === 'CORRECTION' ? '-15000' : '50000'}
          />
        </Field>

        <Field label="Currency" htmlFor="balance-currency" required error={show('currency')}>
          <Input
            id="balance-currency"
            maxLength={3}
            value={state.currency}
            aria-invalid={Boolean(show('currency'))}
            onChange={(event) => set('currency', event.target.value.toUpperCase())}
          />
        </Field>

        <Field label="Date" htmlFor="balance-date" required error={show('occurredAt')} hint="When the money moved, not when it was typed">
          <Input
            id="balance-date"
            type="date"
            value={state.occurredAt}
            aria-invalid={Boolean(show('occurredAt'))}
            onChange={(event) => set('occurredAt', event.target.value)}
          />
        </Field>

        {/* The ₽ value is derived, so a foreign row is unauditable without its
            rate and where that rate came from. */}
        {isRub ? null : (
          <>
            <Field label="FX rate" htmlFor="balance-fx" required error={show('fxRate')} hint="₽ per unit of the currency">
              <Input
                id="balance-fx"
                type="number"
                step="0.000001"
                min="0"
                value={state.fxRate}
                aria-invalid={Boolean(show('fxRate'))}
                onChange={(event) => set('fxRate', event.target.value)}
                placeholder="82.68"
              />
            </Field>
            <Field label="Rate source" htmlFor="balance-rate-source" required error={show('rateSource')}>
              <Select
                id="balance-rate-source"
                value={state.rateSource}
                onChange={(event) => set('rateSource', event.target.value as RateSource)}
              >
                <option value="">— pick one —</option>
                {RATE_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}

        <div className="form-grid__full">
          <Field label="Note" htmlFor="balance-note" hint="What the money was for">
            <Textarea id="balance-note" value={state.note} onChange={(event) => set('note', event.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
